import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { removeFromCart, clearCart } from '../redux/slice/user.slice';
import { fetchActiveDiscounts, fetchPromoCodes } from '../redux/slice/discount.slice';
import { getGamePriceDetails } from '../utils/gamePricing';
import { validatePromoCode, applyPromoCode, createRazorpayOrder, createStandardOrder, clearOrderState, removeAppliedPromo, createStripeOrder, createStripePaymentIntent } from '../redux/slice/order.slice';
import { Trash2, ShoppingBag, ArrowRight, ArrowLeft, Tag, Shield, Award, CheckCircle, Ticket, X, CreditCard, ChevronRight } from 'lucide-react';
import gsap from 'gsap';
import toast from 'react-hot-toast';
import { downloadInvoicePdf } from '../services/invoiceService';
import { loadStripe } from '@stripe/stripe-js';
import StripePaymentModal from '../components/shop/StripePaymentModal';
import { fetchPublicGatewaySettings } from '../redux/slice/gateway.slice';
import LazyImage from '../components/common/LazyImage';
import { FaWindows, FaApple, FaXbox } from 'react-icons/fa';
import { DiAndroid } from 'react-icons/di';
import { SiOculus, SiPlaystation, SiSteamdeck } from 'react-icons/si';
import { BsNintendoSwitch } from 'react-icons/bs';
import { TbDeviceVisionProFilled } from 'react-icons/tb';
import cartAstronautImg from '../assets/images/cart.webp';

const PlatformIconMap = {
    windows: FaWindows,
    playstation: SiPlaystation,
    xbox: FaXbox,
    nintendo: BsNintendoSwitch,
    quest: SiOculus,
    android: DiAndroid,
    ios: FaApple,
    visionpro: TbDeviceVisionProFilled,
    steamdeck: SiSteamdeck
};

const PlatformColorMap = {
    windows: '#0078d7',
    playstation: '#3b82f6',
    xbox: '#107c10',
    nintendo: '#e60012',
    quest: '#ffffff',
    android: '#3ddc84',
    ios: '#a3aaae',
    visionpro: '#a855f7',
    steamdeck: '#60a5fa'
};


const loadRazorpay = () => {
    return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
};

export default function Checkout() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { cart } = useSelector((s) => s.userSlice);
    const { user } = useSelector((s) => s.auth);
    const { discounts } = useSelector((s) => s.discount);

    const { promoCodes, loading: loadingPromos } = useSelector((s) => s.discount);
    const { appliedPromo, loading: orderLoading, success: isSuccess } = useSelector((s) => s.order);
    const { publicSettings } = useSelector((s) => s.gateway);

    const isPurchasingRestricted = () => {
        if (!publicSettings) return false;
        if (publicSettings.purchasePaused) {
            if (publicSettings.purchaseStartsAt) {
                const startsAt = new Date(publicSettings.purchaseStartsAt).getTime();
                return startsAt > Date.now();
            }
            return true;
        }
        return false;
    };

    const restricted = isPurchasingRestricted();

    useEffect(() => {
        if (restricted && !isSuccess) {
            toast.error("Purchases have not started yet.");
            navigate('/cart', { replace: true });
        }
    }, [restricted, navigate, isSuccess]);

    useEffect(() => {
        dispatch(clearOrderState());
        dispatch(fetchActiveDiscounts());
    }, [dispatch]);

    const cartTotal = cart.reduce((acc, item) => {
        const details = getGamePriceDetails(item.game, item.platform, discounts);
        return acc + details.price;
    }, 0);
    const taxRatePercent = publicSettings?.taxRate ?? 10;
    const tax = Number((cartTotal * (taxRatePercent / 100)).toFixed(2));
    const [promoCode, setPromoCode] = useState('');
    const [isApplyingPromo, setIsApplyingPromo] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isOrderFinalizing, setIsOrderFinalizing] = useState(false);
    const [showOffersModal, setShowOffersModal] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('razorpay');
    const [showStripeModal, setShowStripeModal] = useState(false);
    const [stripeClientSecret, setStripeClientSecret] = useState('');
    const [stripePromise, setStripePromise] = useState(null);
    const hasAnimatedSuccessRef = useRef(false);

    // P20 – Only fetch if settings haven't been loaded yet (StoreSynchronizer already calls this on mount)
    useEffect(() => {
        if (!publicSettings) {
            dispatch(fetchPublicGatewaySettings());
        }
    }, [dispatch, publicSettings]);

    // P20 – Use a ref to ensure loadStripe is only called once per key (loadStripe memoizes internally, but guard for safety)
    const stripeKeyRef = useRef(null);
    useEffect(() => {
        if (publicSettings?.stripePublishableKey && stripeKeyRef.current !== publicSettings.stripePublishableKey) {
            stripeKeyRef.current = publicSettings.stripePublishableKey;
            setStripePromise(loadStripe(publicSettings.stripePublishableKey));
        }
        if (publicSettings?.activeGateway) {
            setPaymentMethod(publicSettings.activeGateway.toLowerCase());
        }
    }, [publicSettings]);

    const discountAmount = appliedPromo ? appliedPromo.discountAmount : 0;
    const total = cartTotal + tax - discountAmount;

    const buildCheckoutItems = () =>
        cart.map((item) => ({
            game: item.game._id || item.game.id,
            quantity: 1,
            platform: item.platform || 'windows',
        }));

    const appliedPromoCode = appliedPromo ? appliedPromo.discountCode : undefined;

    const headerRef = useRef(null);
    const successRef = useRef(null);

    // Fetch active promocodes on mount/login
    useEffect(() => {
        if (user) {
            dispatch(fetchPromoCodes());
        }
    }, [user, dispatch]);

    // Prevent scrolling when offers modal is open
    useEffect(() => {
        if (showOffersModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [showOffersModal]);

    // Automatically recalculate or invalidate coupon if cart contents or total change
    useEffect(() => {
        if (appliedPromo) {
            const currentPromoObj = promoCodes.find(p => p.code === appliedPromo.discountCode);
            if (currentPromoObj && cartTotal < currentPromoObj.minOrderValue) {
                dispatch(removeAppliedPromo());
                setPromoCode('');
                toast.error(`Promo code ${appliedPromo.discountCode} removed (minimum order value of $${currentPromoObj.minOrderValue} not met)`);
            } else if (cart.length === 0) {
                dispatch(removeAppliedPromo());
                setPromoCode('');
            } else {
                const reapplyPromo = async () => {
                    try {
                        await dispatch(applyPromoCode({
                            code: appliedPromo.discountCode,
                            orderValue: cartTotal,
                            items: cart
                        })).unwrap();
                    } catch (e) {
                        dispatch(removeAppliedPromo());
                        setPromoCode('');
                    }
                };
                reapplyPromo();
            }
        }
    }, [cartTotal, cart.length, promoCodes, dispatch]);

    const handleApplyPromoCode = async (code) => {
        setIsApplyingPromo(true);
        try {
            // First validate code exists and meets general criteria
            await dispatch(validatePromoCode({
                code: code,
                orderValue: cartTotal
            })).unwrap();

            // Then apply and calculate discounts
            await dispatch(applyPromoCode({
                code: code,
                orderValue: cartTotal,
                items: cart
            })).unwrap();
            setPromoCode(code);
            toast.success('Promo code validated and applied!');
        } catch (error) {
            toast.error(error || 'Invalid or expired promo code');
        } finally {
            setIsApplyingPromo(false);
        }
    };

    useEffect(() => {
        if (headerRef.current) {
            gsap.fromTo(headerRef.current,
                { opacity: 0, y: 30 },
                { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' }
            );
        }
    }, []);

    useEffect(() => {
        if ((isSuccess || isOrderFinalizing) && successRef.current && !hasAnimatedSuccessRef.current) {
            hasAnimatedSuccessRef.current = true;
            gsap.fromTo(successRef.current,
                { opacity: 0, scale: 0.9, y: 20 },
                { opacity: 1, scale: 1, y: 0, duration: 0.6, ease: 'back.out(1.7)' }
            );
        }
    }, [isSuccess, isOrderFinalizing]);

    const handleRemovePromo = () => {
        dispatch(removeAppliedPromo());
        setPromoCode('');
        toast.success('Promo code removed');
    };

    const handleApplyPromo = async () => {
        if (!promoCode.trim()) return toast.error('Please enter a promo code');
        setIsApplyingPromo(true);
        try {
            // First validate code exists and meets general criteria
            await dispatch(validatePromoCode({
                code: promoCode.trim(),
                orderValue: cartTotal
            })).unwrap();

            // Then apply and calculate discounts
            await dispatch(applyPromoCode({
                code: promoCode.trim(),
                orderValue: cartTotal,
                items: cart
            })).unwrap();
            toast.success('Promo code validated and applied!');
        } catch (error) {
            toast.error(error || 'Invalid or expired promo code');
            dispatch(removeAppliedPromo());
        } finally {
            setIsApplyingPromo(false);
        }
    };

    const handleRazorpayPayment = async () => {
        if (!user) {
            toast.error('Please login to proceed with checkout');
            navigate('/login');
            return;
        }
        setIsProcessing(true);
        const sdkLoaded = await loadRazorpay();

        if (!sdkLoaded) {
            toast.error('Razorpay SDK failed to load. Check your connection.');
            setIsProcessing(false);
            return;
        }

        try {
            const razorpayRes = await dispatch(createRazorpayOrder({
                items: buildCheckoutItems(),
                promoCode: appliedPromoCode,
            })).unwrap();
            const { order, key_id } = razorpayRes;

            const options = {
                key: key_id,
                amount: order.amount,
                currency: 'USD',
                name: 'Cube Games',
                description: 'Game Purchase',
                order_id: order.id,
                handler: async (response) => {
                    setIsOrderFinalizing(true);
                    try {
                        const verifyPayload = {
                            razorpayOrderId: response.razorpay_order_id,
                            razorpayPaymentId: response.razorpay_payment_id,
                            razorpaySignature: response.razorpay_signature,
                            items: buildCheckoutItems(),
                            promoCode: appliedPromoCode,
                        };

                        const verifyRes = await dispatch(createStandardOrder(verifyPayload)).unwrap();
                        if (verifyRes.success) {
                            const populatedItems = cart.map(item => {
                                const plat = item.platform || 'windows';
                                const details = getGamePriceDetails(item.game, plat, discounts);
                                return {
                                    game: item.game,
                                    quantity: 1,
                                    platform: plat,
                                    price: details.price
                                };
                            });

                            downloadInvoicePdf(verifyRes.order, user, populatedItems);
                            dispatch(clearCart());
                            toast.success('Order placed successfully!');
                        } else {
                            toast.error(verifyRes.message || 'Payment verification failed');
                            setIsOrderFinalizing(false);
                        }
                    } catch (error) {
                        toast.error(error || 'Payment verification failed');
                        setIsOrderFinalizing(false);
                    } finally {
                        setIsProcessing(false);
                    }
                },
                prefill: {
                    name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username,
                    email: user.email,
                },
                theme: {
                    color: "#00f0ff"
                },
                modal: {
                    ondismiss: function () {
                        setIsProcessing(false);
                    }
                }
            };

            const paymentObject = new window.Razorpay(options);
            paymentObject.on('payment.failed', function (response) {
                toast.error(`Payment failed: ${response.error.description}`);
                setIsProcessing(false);
                setIsOrderFinalizing(false);
            });
            paymentObject.open();

        } catch (error) {
            console.error('Razorpay Error:', error);
            toast.error(error || 'Error initiating payment.');
            setIsProcessing(false);
            setIsOrderFinalizing(false);
        }
    };

    const handleFreeCheckout = async () => {
        if (!user) {
            toast.error('Please login to proceed with checkout');
            navigate('/login');
            return;
        }
        setIsProcessing(true);
        setIsOrderFinalizing(true);
        try {
            const orderPayload = {
                items: buildCheckoutItems(),
                promoCode: appliedPromoCode,
            };

            const verifyRes = await dispatch(createStandardOrder(orderPayload)).unwrap();
            if (verifyRes.success) {
                const populatedItems = cart.map(item => {
                    const plat = item.platform || 'windows';
                    const details = getGamePriceDetails(item.game, plat, discounts);
                    return {
                        game: item.game,
                        quantity: 1,
                        platform: plat,
                        price: details.price
                    };
                });

                downloadInvoicePdf(verifyRes.order, user, populatedItems);
                dispatch(clearCart());
                toast.success('Order placed successfully!');
            } else {
                toast.error(verifyRes.message || 'Order creation failed');
                setIsOrderFinalizing(false);
            }
        } catch (error) {
            toast.error(error || 'Order creation failed. Contact support.');
            setIsOrderFinalizing(false);
        } finally {
            setIsProcessing(false);
        }
    }

    const handleStripePayment = async () => {
        if (!user) {
            toast.error('Please login to proceed with checkout');
            navigate('/login');
            return;
        }
        setIsProcessing(true);

        try {
            const secret = await dispatch(createStripePaymentIntent({
                items: buildCheckoutItems(),
                promoCode: appliedPromoCode,
            })).unwrap();
            setStripeClientSecret(secret);
            setShowStripeModal(true);
        } catch (error) {
            console.error('Stripe Error:', error);
            toast.error(error || 'Error initiating payment.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleStripeSuccess = async (paymentIntent) => {
        setIsOrderFinalizing(true);
        setShowStripeModal(false);
        try {
            const orderPayload = {
                items: buildCheckoutItems(),
                promoCode: appliedPromoCode,
                stripePaymentIntentId: paymentIntent.id
            };

            const verifyRes = await dispatch(createStripeOrder(orderPayload)).unwrap();
            if (verifyRes.success) {
                const populatedItems = cart.map(item => {
                    const plat = item.platform || 'windows';
                    const details = getGamePriceDetails(item.game, plat, discounts);
                    return {
                        game: item.game,
                        quantity: 1,
                        platform: plat,
                        price: details.price
                    };
                });

                downloadInvoicePdf(verifyRes.order, user, populatedItems);
                dispatch(clearCart());
                toast.success('Order placed successfully!');
            } else {
                toast.error(verifyRes.message || 'Order creation failed');
                setIsOrderFinalizing(false);
            }
        } catch (error) {
            toast.error(error || 'Order creation failed. Contact support.');
            setIsOrderFinalizing(false);
        }
    };

    // ── Success Screen ──
    if (isSuccess || isOrderFinalizing) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4 py-28 relative">
                <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-cyber-green/5 rounded-full blur-[140px] pointer-events-none"></div>
                <div ref={successRef} className="w-full max-w-md text-center">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#10e760] via-[#00f5a0] to-[#00e676] flex items-center justify-center mx-auto mb-6 shadow-[0_0_35px_rgba(16,231,96,0.35)]">
                        <CheckCircle className="w-10 h-10 text-white" />
                    </div>
                    <p className="text-[10px] font-cyber font-medium text-cyber-green uppercase tracking-[0.25em] mb-2">Order Confirmed</p>
                    <h1 className="font-cyber font-semibold text-3xl text-white uppercase mb-3">Payment Successful</h1>
                    <p className="text-gray-500 text-sm font-sans mb-8 leading-relaxed">
                        Your games have been added to your library. Check your email for the receipt and download links.
                    </p>
                    <div className="space-y-3">
                        <button onClick={() => navigate('/library')} className="cyber-btn w-full !py-4">
                            VIEW MY LIBRARY
                        </button>
                        <button onClick={() => navigate('/')}
                            className="w-full py-3 rounded-xl bg-white/5 border border-white/10 hover:border-cyber-cyan/40 text-gray-400 hover:text-white text-xs font-cyber font-medium uppercase tracking-wider transition-all">
                            BACK TO HOME
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-24 pb-12 sm:pt-32 sm:pb-32 relative">
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-cyber-blue/4 rounded-full blur-[140px] pointer-events-none"></div>

            <div className="container mx-auto">
                <div ref={headerRef} className="mb-6 sm:mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4 sm:gap-6 relative z-10">
                    <div className="flex-shrink-0">
                        <button
                            onClick={() => {
                                if (window.history.state && window.history.state.idx > 0) {
                                    navigate(-1);
                                } else {
                                    navigate('/all-games');
                                }
                            }}
                            className="inline-flex items-center justify-center gap-2 mb-4 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl bg-[#151a26] border border-white/[0.08] text-xs font-cyber font-bold text-gray-200 hover:text-white hover:border-white/20 hover:bg-[#1a2130] transition-all duration-300 uppercase tracking-wider group/btn shadow-sm cursor-pointer"
                        >
                            <ArrowLeft className="w-3.5 h-3.5 group-hover/btn:-translate-x-1 transition-transform" />
                            <span>BACK</span>
                        </button>
                        <p className="text-[10px] font-cyber font-semibold text-cyber-cyan uppercase tracking-[0.25em] mb-2 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse"></span>
                            Shopping Cart
                        </p>
                        <h1 className="font-cyber font-semibold text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight whitespace-nowrap">
                            YOUR <span className="animate-gradient-text drop-shadow-[0_0_20px_rgba(0,240,255,0.35)]">CART</span>
                        </h1>
                        <div className="h-[2px] w-20 animate-gradient-bg mt-3 rounded-full"></div>
                    </div>
                </div>
                <style>{`
                    @keyframes astronautFloat {
                        0%, 100% { transform: translateY(0px) rotate(0deg); }
                        50% { transform: translateY(-14px) rotate(1.8deg); }
                    }
                    .animate-astronaut-float {
                        animation: astronautFloat 3.8s ease-in-out infinite;
                    }
                `}</style>

                {cart.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 sm:py-12 text-center max-w-lg mx-auto space-y-4 px-4">
                        <div className="relative group w-full flex items-center justify-center p-1 sm:p-2">
                            {/* Dark Teal Radial Glow Backdrop */}
                            <div
                                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-60 xs:w-68 sm:w-80 h-60 xs:h-68 sm:h-80 rounded-full blur-2xl pointer-events-none opacity-90"
                            />
                            <img
                                src={cartAstronautImg}
                                alt="Cart Empty Astronaut"
                                className="w-36 xs:w-44 sm:w-52 md:w-56 max-w-[240px] max-h-[250px] h-auto object-contain relative z-10 animate-astronaut-float cursor-pointer transition-transform duration-500 group-hover:scale-105"
                            />
                        </div>

                        <div className="space-y-2.5 max-w-md">
                            <h2 className="font-cyber font-black text-2xl md600:text-3xl text-[#00f0ff] uppercase tracking-wider drop-shadow-[0_0_18px_rgba(0,240,255,0.4)]">
                                YOUR CART IS EMPTY
                            </h2>
                            <p className="text-xs md600:text-sm text-gray-300 font-sans leading-relaxed">
                                Looks like your cart is empty! Add some games to your library and experience next-gen gaming.
                            </p>
                        </div>

                        <button
                            onClick={() => navigate('/all-games')}
                            className="w-full sm:w-auto relative overflow-hidden inline-flex items-center justify-center gap-2.5 px-6 py-2.5 sm:px-7 sm:py-3 rounded-xl bg-gradient-to-r from-[#00f0ff] via-[#5c60ff] to-[#bd00ff] text-black font-cyber font-bold text-xs sm:text-[13px] uppercase tracking-wider hover:scale-105 active:scale-95 transition-all duration-300 whitespace-nowrap group before:absolute before:top-0 before:-left-[100%] before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/40 before:to-transparent before:transition-all before:duration-700 hover:before:left-[100%]"
                        >
                            <span>DISCOVER GAMES</span>
                            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Cart Items */}
                        <div className="lg:col-span-2 space-y-4">
                            {cart.map(({ game, quantity, platform }) => {
                                const details = getGamePriceDetails(game, platform, discounts);
                                const selectedPlat = (platform || 'windows').toLowerCase();
                                const PlatformIcon = PlatformIconMap[selectedPlat] || FaWindows;
                                return (
                                    <div key={`${game._id || game.id}-${platform || 'windows'}`} className="relative rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02] p-3 md600:p-4 flex gap-3 md600:gap-4 group hover:border-cyber-cyan/30 transition-all">
                                        <LazyImage
                                            src={game.cover_image?.url || game.cover_image || game.image?.url || game.image}
                                            placeholder={game.cover_image?.lowUrl || game.image?.lowUrl}
                                            alt={game.title}
                                            className="w-full h-full object-cover"
                                            containerClassName="w-20 h-20 md600:w-24 md600:h-24 rounded-xl overflow-hidden flex-shrink-0"
                                        />
                                        <div className="flex-1 min-w-0 pr-10 flex flex-col justify-center gap-1">
                                            {/* Platform Icon & Category Name */}
                                            <div className="flex items-center gap-2 text-gray-400 text-xs md600:text-md600 font-sans font-medium">
                                                {PlatformIcon && (
                                                    <PlatformIcon
                                                        className="w-4 h-4 md600:w-4.5 md600:h-4.5 flex-shrink-0"
                                                        style={{ color: PlatformColorMap[selectedPlat] || '#3b82f6' }}
                                                    />
                                                )}
                                                <span className="truncate capitalize">{game.category?.name || 'Game'}</span>
                                            </div>

                                            {/* Title */}
                                            <h3 className="font-cyber font-bold text-xs md600:text-base text-white tracking-tight">
                                                {game.title}
                                            </h3>

                                            {/* Price */}
                                            <div className="flex items-baseline gap-2 pt-0.5">
                                                <span className="text-cyber-cyan font-cyber font-extrabold text-base md600:text-lg">
                                                    ${details.price.toFixed(2)}
                                                </span>
                                                {details.discount > 0 && (
                                                    <>
                                                        <span className="text-xs text-gray-500 line-through font-mono">
                                                            ${details.originalPrice.toFixed(2)}
                                                        </span>
                                                        <span className="text-cyber-green text-[9px] font-cyber font-bold">-{details.discount}% OFF</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <button onClick={() => dispatch(removeFromCart({ gameId: game._id || game.id, platform: platform || 'windows' }))}
                                            className="absolute top-3 right-3 p-1.5 md600:p-2 rounded-lg bg-white/5 hover:bg-cyber-pink/20 border border-white/10 hover:border-cyber-pink/40 text-gray-500 hover:text-cyber-pink transition-all">
                                            <Trash2 className="w-3.5 h-3.5 md600:w-4 md600:h-4" />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Order Summary */}
                        <div className="relative rounded-2xl overflow-hidden h-fit"
                            style={{ background: 'linear-gradient(135deg, #0c0c18 0%, #080810 100%)', boxShadow: '0 0 0 1px rgba(255,255,255,0.06), 0 20px 60px rgba(0,0,0,0.5)' }}>
                            <div className="absolute top-0 left-0 w-full h-[1.5px] bg-gradient-to-r from-cyber-blue via-cyber-cyan to-cyber-purple opacity-70"></div>
                            <div className="absolute inset-0 opacity-[0.02] pointer-events-none"
                                style={{ backgroundImage: 'radial-gradient(circle, rgba(0,240,255,0.8) 1px, transparent 1px)', backgroundSize: '28px 28px' }}></div>

                            <div className="relative z-10 p-6 space-y-5">
                                <p className="text-[10px] font-cyber font-medium text-gray-500 uppercase tracking-widest">ORDER SUMMARY</p>

                                <div className="space-y-3 py-4 border-y border-white/5">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-500 font-cyber">SUBTOTAL</span>
                                        <span className="text-white font-cyber font-medium">${cartTotal.toFixed(2)}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-500 font-cyber">TAX ({taxRatePercent}%)</span>
                                        <span className="text-white font-cyber font-medium">${tax.toFixed(2)}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-500 font-cyber">DISCOUNT</span>
                                        <span className="text-cyber-green font-cyber font-medium">-${discountAmount.toFixed(2)}</span>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between py-1">
                                    <span className="text-xs font-cyber font-medium text-gray-400 uppercase tracking-widest">TOTAL</span>
                                    <span className="text-2xl font-cyber font-semibold text-white">${total.toFixed(2)}</span>
                                </div>

                                {/* Available Offers Link Button */}
                                {user && promoCodes.length > 0 && (
                                    <button
                                        onClick={() => setShowOffersModal(true)}
                                        className="w-full py-2.5 rounded-xl bg-cyber-purple/10 hover:bg-cyber-purple/20 border border-cyber-purple/30 hover:border-cyber-purple/50 text-cyber-purple hover:text-white text-[10px] font-cyber font-bold uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-2"
                                    >
                                        <Ticket className="w-3.5 h-3.5" />
                                        Available Offers ({promoCodes.length})
                                    </button>
                                )}

                                {/* Promo Code */}
                                {appliedPromo ? (
                                    <div className="flex items-center justify-between p-3 rounded-xl bg-cyber-green/10 border border-cyber-green/30">
                                        <div className="flex items-center gap-2">
                                            <Ticket className="w-4 h-4 text-cyber-green" />
                                            <span className="text-cyber-green font-cyber font-medium text-xs uppercase tracking-wider">{appliedPromo.discountCode}</span>
                                        </div>
                                        <button onClick={handleRemovePromo} className="text-gray-400 hover:text-cyber-pink text-xs font-cyber transition-colors">
                                            REMOVE
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600 pointer-events-none" />
                                            <input
                                                type="text"
                                                placeholder="Promo code"
                                                value={promoCode}
                                                onChange={(e) => setPromoCode(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                                                className="w-full pl-9 pr-3 py-2.5 rounded-xl text-xs font-cyber text-white placeholder-gray-600 bg-white/[0.04] border border-white/10 focus:border-cyber-purple/50 focus:outline-none transition-all uppercase tracking-wider"
                                            />
                                        </div>
                                        <button
                                            onClick={handleApplyPromo}
                                            disabled={isApplyingPromo || !promoCode.trim()}
                                            className="px-4 py-2.5 rounded-xl bg-cyber-purple/20 hover:bg-cyber-purple/30 border border-cyber-purple/30 hover:border-cyber-purple/60 text-cyber-purple text-xs font-cyber font-medium uppercase tracking-wider transition-all disabled:opacity-40 disabled:pointer-events-none"
                                        >
                                            {isApplyingPromo ? '...' : 'APPLY'}
                                        </button>
                                    </div>
                                )}



                                <button
                                    onClick={total === 0 ? handleFreeCheckout : (paymentMethod === 'stripe' ? handleStripePayment : handleRazorpayPayment)}
                                    disabled={isProcessing || cart.length === 0 || restricted}
                                    className="cyber-btn w-full !py-4 flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none"
                                >
                                    {isProcessing ? 'PROCESSING...' : (restricted ? 'PURCHASING RESTRICTED' : <>PROCEED TO CHECKOUT <ArrowRight className="w-4 h-4" /></>)}
                                </button>

                                <div className="space-y-2 pt-4 border-t border-white/5">
                                    <div className="flex items-center gap-2 text-xs text-gray-600">
                                        <Shield className="w-3.5 h-3.5 text-cyber-blue" />
                                        <span className="font-mono">Secure checkout</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-gray-600">
                                        <Award className="w-3.5 h-3.5 text-cyber-blue" />
                                        <span className="font-mono">Instant delivery</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-gray-600">
                                        <Tag className="w-3.5 h-3.5 text-cyber-blue" />
                                        <span className="font-mono">Lifetime access</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Promo Codes Available Offers Modal */}
            {showOffersModal && (
                <div className="fixed inset-0 z-[999] bg-[#050508]/85 backdrop-blur-md flex items-center justify-center p-4">
                    <div className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-[#0c0c18] p-6 space-y-5 shadow-[0_0_50px_rgba(0,0,0,0.8)] overflow-hidden">
                        {/* Top neon line accent */}
                        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-cyber-blue via-cyber-cyan to-cyber-purple"></div>

                        {/* Modal Header */}
                        <div className="flex items-start justify-between">
                            <div>
                                <h3 className="font-cyber font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                                    <Ticket className="w-5 h-5 text-cyber-purple animate-pulse" />
                                    Available Offers
                                </h3>
                                <p className="text-gray-500 text-xs font-sans mt-1">
                                    Power up your cart with exclusive futuristic coupon deals.
                                </p>
                            </div>
                            <button
                                onClick={() => setShowOffersModal(false)}
                                className="p-1.5 rounded-lg bg-white/5 border border-white/10 hover:border-cyber-pink/40 hover:bg-cyber-pink/10 text-gray-400 hover:text-cyber-pink transition-all"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Offers List Container */}
                        <div data-lenis-prevent className="space-y-3.5 max-h-[380px] overflow-y-auto pr-1">
                            {promoCodes.map((promo) => {
                                const isApplied = appliedPromo && appliedPromo.discountCode === promo.code;
                                const canApply = cartTotal >= promo.minOrderValue;
                                const requiredMore = promo.minOrderValue - cartTotal;

                                return (
                                    <div
                                        key={promo._id}
                                        onClick={() => {
                                            if (canApply && !isApplied) {
                                                handleApplyPromoCode(promo.code);
                                                setShowOffersModal(false);
                                            }
                                        }}
                                        className={`relative rounded-xl border p-4 flex flex-col justify-between transition-all duration-300 group
                                            ${isApplied
                                                ? 'bg-cyber-green/5 border-cyber-green/50 shadow-[0_0_15px_rgba(57,255,20,0.1)] cursor-default'
                                                : canApply
                                                    ? 'bg-white/[0.01] border-white/10 hover:border-cyber-purple/40 hover:bg-white/[0.03] cursor-pointer'
                                                    : 'bg-white/[0.003] border-white/5 opacity-50 cursor-not-allowed'}`}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                                <span className="px-2.5 py-0.5 rounded text-[10px] font-cyber font-black tracking-wider bg-cyber-purple/20 text-cyber-purple uppercase border border-cyber-purple/30">
                                                    {promo.code}
                                                </span>
                                            </div>
                                            <span className="text-sm font-cyber font-bold text-cyber-cyan">
                                                {promo.type === 'percentage' ? `${promo.discount}% OFF` : `$${promo.discount} OFF`}
                                            </span>
                                        </div>

                                        {promo.description && (
                                            <p className="text-xs text-gray-500 leading-relaxed font-sans mt-2">
                                                {promo.description}
                                            </p>
                                        )}

                                        <div className="flex items-center justify-between mt-4 pt-2.5 border-t border-white/5">
                                            <div className="text-[9px] font-mono text-gray-500 flex gap-3">
                                                <span>MIN: ${promo.minOrderValue}</span>
                                                {promo.maxUses > 0 && <span>MAX USES: {promo.maxUses}</span>}
                                            </div>

                                            {isApplied ? (
                                                <span className="text-[10px] font-cyber font-bold text-cyber-green tracking-wider uppercase flex items-center gap-1">
                                                    <CheckCircle className="w-3.5 h-3.5" /> APPLIED
                                                </span>
                                            ) : canApply ? (
                                                <span className="text-[10px] font-cyber font-bold text-cyber-purple group-hover:text-white tracking-widest uppercase">
                                                    APPLY COUPON
                                                </span>
                                            ) : (
                                                <span className="text-[9px] font-cyber font-bold text-cyber-pink bg-cyber-pink/5 border border-cyber-pink/20 px-2 py-0.5 rounded uppercase">
                                                    LOCKED (+${requiredMore.toFixed(0)})
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
            {/* Stripe Payment Modal */}
            <StripePaymentModal
                isOpen={showStripeModal}
                onClose={() => setShowStripeModal(false)}
                amount={total}
                clientSecret={stripeClientSecret}
                stripePromise={stripePromise}
                onSuccess={handleStripeSuccess}
            />
        </div>
    );
}